import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * ValidationService
 *
 * Zentrale Validierungslogik für wiederverwendbare Feld-Validierungen.
 * Wird sowohl in Reactive Forms (als ValidatorFn) als auch
 * in Signal-basierter Validierung (als statische Methode) verwendet.
 */
@Injectable({
  providedIn: 'root',
})
export class ValidationService {

  /**
   * Prüft ob ein gegebener String eine gültige URL ist.
   * Leere Strings gelten als gültig (Feld ist optional).
   *
   * @returns `true` wenn die URL gültig oder leer ist, `false` bei ungültiger URL.
   */
  static isValidUrl(value: string | null | undefined): boolean {
    if (!value || value.trim().length === 0) return true;
    try {
      new URL(value);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Prüft ob ein gegebener String eine ungültige (nicht-leere) URL ist.
   * Gegenstück zu `isValidUrl` – praktisch für Error-Flags in Signals.
   *
   * @returns `true` wenn die URL ungültig ist, `false` wenn sie gültig oder leer ist.
   */
  static hasUrlError(value: string | null | undefined): boolean {
    return !ValidationService.isValidUrl(value);
  }

  /**
   * Angular Reactive Forms ValidatorFn für URL-Felder.
   * Gibt einen `invalidUrl`-Fehler zurück, wenn der Wert keine gültige URL ist.
   * Leere Werte werden als gültig behandelt (optional).
   */
  static urlValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value || (typeof value === 'string' && value.trim().length === 0)) {
        return null; // Leer = gültig (optional)
      }
      try {
        new URL(value);
        return null;
      } catch {
        return { invalidUrl: true };
      }
    };
  }

  /**
   * Angular Reactive Forms ValidatorFn für lokale Windows-Pfade (nur P:\).
   * Gibt einen `invalidPath`-Fehler zurück, wenn der Wert kein gültiger P:\-Pfad ist.
   * Leere Werte werden als gültig behandelt (optional).
   */
  static localPathValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value || (typeof value === 'string' && value.trim().length === 0)) {
        return null; // Leer = gültig (optional)
      }
      return /^P:\\.+/i.test(value.trim()) ? null : { invalidPath: true };
    };
  }

  /**
   * Cross-Field-Validator: Stellt sicher, dass nur entweder SharePoint oder Laufwerk P
   * befüllt ist – nicht beide gleichzeitig.
   * Wird auf FormGroup-Ebene angewendet.
   *
   * @param sharepointKey – FormControl-Name für SharePoint-Pfad
   * @param directoryPKey – FormControl-Name für Laufwerk-P-Pfad
   */
  static exclusivePathValidator(
    sharepointKey = 'sharepointFolderPath',
    directoryPKey = 'directoryPFolderPath'
  ): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const sp = group.get(sharepointKey)?.value?.trim();
      const dp = group.get(directoryPKey)?.value?.trim();
      if (sp && dp) {
        return { exclusivePath: true };
      }
      return null;
    };
  }
}

